// OSS-verified applications (arc 101 slice 07) — `docs/idea/billing.md`
// § "OSS-verified flow", step 1: *"Apply via dashboard … Manual review by us."*
//
// Verified OSS orgs pay NOTHING, so the verdict can never be a rule the
// applicant can satisfy on their own: it is a human review, and this row is the
// queue entry that review works from. A maintainer applies with the project
// (`owner/repo`) and free-text EVIDENCE (license, stars, critical-infra listing,
// maintainer history — the requirements in that doc); an operator approves or
// rejects it.
//
// APPROVAL is what flips the account to `Tier.OssVerified`, which stamps
// `verifiedOssAt` (see `ossVerifiedConsistent` in `./account`) — the tier and
// the review record are one act, never two. Nothing here grants anything by
// itself: a `pending` row is an ask, not an entitlement.
//
// Pure types — no I/O.

import { z } from 'zod';
import { RepoFullNameSchema } from '../value-objects/task-envelope';

/** Where an application stands. `pending` is the only state an operator acts on. */
export const OssVerificationStatus = {
  /** Submitted, awaiting operator review — the admin queue's contents. */
  Pending: 'pending',
  /** Reviewed + granted; the account was moved to `oss_verified` in the same act. */
  Approved: 'approved',
  /** Reviewed + refused. `reviewerNote` carries the why, so the applicant can re-apply. */
  Rejected: 'rejected',
} as const;
export const OssVerificationStatusSchema = z.enum(OssVerificationStatus);
export type OssVerificationStatus = z.infer<typeof OssVerificationStatusSchema>;

/**
 * Evidence bounds. The floor keeps "please verify us" out of the queue (a
 * reviewer needs something to check); the ceiling bounds a free-text column
 * written by an unattended API caller. Enforced at the edge (`apps/api`) and
 * re-asserted here so both transports agree.
 */
export const OSS_EVIDENCE_MIN_CHARS = 20;
export const OSS_EVIDENCE_MAX_CHARS = 4000;

/** Free-text case for verification — links, license, stars, maintainer history. */
export const OssEvidenceSchema = z
  .string()
  .trim()
  .min(OSS_EVIDENCE_MIN_CHARS)
  .max(OSS_EVIDENCE_MAX_CHARS);

/** Operator's verdict note — shown back to the applicant, so bounded like evidence. */
export const OSS_REVIEWER_NOTE_MAX_CHARS = 1000;
export const OssReviewerNoteSchema = z.string().trim().min(1).max(OSS_REVIEWER_NOTE_MAX_CHARS);

/** What an applicant submits (the dashboard form's two fields). */
export const OssVerificationApplySchema = z.object({
  repoFullName: RepoFullNameSchema,
  evidence: OssEvidenceSchema,
});

/**
 * One application row (`oss_verification_requests`). `submittedByUserId` is the
 * human who applied (absent for a legacy account-only session or a PAT);
 * `reviewerNote` + `decidedAt` are set together by the operator verdict and are
 * null while `pending`.
 *
 * The stored row reuses the SAME field schemas as the ask that created it
 * (`RepoFullNameSchema` / `OssEvidenceSchema` / `OssReviewerNoteSchema`) — a row
 * is never looser than its application, so a project that could not be applied
 * for can't be read back either.
 */
export const OssVerificationRequestSchema = z
  .object({
    id: z.string(),
    accountId: z.string(),
    repoFullName: RepoFullNameSchema,
    evidence: OssEvidenceSchema,
    status: OssVerificationStatusSchema,
    submittedByUserId: z.string().nullable(),
    reviewerNote: OssReviewerNoteSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    decidedAt: z.date().nullable(),
  })
  // A verdict is a single act: `decidedAt` and `reviewerNote` are verdict
  // artifacts, so they exist iff the row left `pending`. Blocks the half-states
  // (a decided row still queued; a pending row that claims a review date or
  // carries a reviewer's note) before any of them can be written or trusted.
  //
  // The one asymmetry: a REJECT must carry its note. The note is the only thing
  // the applicant is shown, and it is what they re-apply against — a refusal
  // with no reason is a dead end. An APPROVE needs none: the grant (the account
  // moving to `oss_verified`) is its own explanation.
  .superRefine((r, ctx) => {
    const pending = r.status === OssVerificationStatus.Pending;
    if (pending && r.decidedAt !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['decidedAt'],
        message: 'a pending application carries no decidedAt',
      });
    }
    if (!pending && r.decidedAt === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['decidedAt'],
        message: 'a decided application must carry decidedAt',
      });
    }
    if (pending && r.reviewerNote !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reviewerNote'],
        message: 'a pending application carries no reviewerNote',
      });
    }
    if (r.status === OssVerificationStatus.Rejected && r.reviewerNote === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reviewerNote'],
        message: 'a rejected application must carry the reviewerNote shown to the applicant',
      });
    }
  });
export type OssVerificationRequest = z.infer<typeof OssVerificationRequestSchema>;

/** An operator's verdict on one application. */
export const OssVerificationDecision = {
  Approve: 'approve',
  Reject: 'reject',
} as const;
export const OssVerificationDecisionSchema = z.enum(OssVerificationDecision);
export type OssVerificationDecision = z.infer<typeof OssVerificationDecisionSchema>;

/**
 * The terminal status a verdict lands the row in. One mapping, so the REST
 * projection, the MCP verb, and the store never disagree about what `approve`
 * means.
 */
export function statusForDecision(decision: OssVerificationDecision): OssVerificationStatus {
  return decision === OssVerificationDecision.Approve
    ? OssVerificationStatus.Approved
    : OssVerificationStatus.Rejected;
}
